/**
 * Spécifications d'étiquettes CLKI — alignées sur barcodeCatalogueService (caisse).
 * Prix variante : (prixPromo ?? prix) + (Number(choix.prix) || 0)
 */

export function roundRetailUnitPrice(value) {
  if (value == null || Number.isNaN(Number(value))) return null;
  return Math.round(Number(value) * 100) / 100;
}

/** Même base que parent.prixPromo ?? parent.prix côté API caisse. */
export function resolveParentBaseUnitPrice(prix, prixPromo) {
  const promoRaw = prixPromo;
  if (promoRaw != null && promoRaw !== '') {
    const promo = Number(promoRaw);
    if (!Number.isNaN(promo)) return roundRetailUnitPrice(promo);
  }
  const base = Number(prix);
  if (Number.isNaN(base)) return null;
  return roundRetailUnitPrice(base);
}

export function computeChoiceUnitPrice(prix, prixPromo, choixPrixSupplement) {
  const base = resolveParentBaseUnitPrice(prix, prixPromo);
  if (base == null) return null;
  const sup = Number(choixPrixSupplement);
  const add = Number.isNaN(sup) ? 0 : sup;
  return roundRetailUnitPrice(base + add);
}

export function formatRetailPriceLabel(unitPrice) {
  if (unitPrice == null || Number.isNaN(Number(unitPrice))) return null;
  return `${Number(unitPrice).toFixed(2).replace('.', ',')} €`;
}

function isOptionMandatory(option) {
  return Boolean(option?.obligatoire);
}

function mandatoryOptions(options) {
  return (options || []).filter(isOptionMandatory);
}

function choixHasBarcode(choix, choixInternalById) {
  const fromMap = choix?.id && choixInternalById?.[choix.id];
  const code = fromMap?.code || choix?.internalBarcode?.code;
  return Boolean(code);
}

function resolveChoixBarcode(choix, choixInternalById) {
  const fromMap = choix?.id && choixInternalById?.[choix.id];
  return fromMap?.code || choix?.internalBarcode?.code || null;
}

/**
 * Parent exclu par défaut si options obligatoires + au moins une variante CLKI :
 * vente typique au niveau variante (ex. conditionnements exclusifs).
 * Présence d'options facultatives seules ne masque pas le parent.
 */
export function shouldIncludeParentLabelByDefault(options, { parentHasBarcode, anyChoixHasBarcode }) {
  if (!parentHasBarcode) return false;
  if (mandatoryOptions(options).length === 0) return true;
  if (!anyChoixHasBarcode) return true;
  return false;
}

/**
 * Prix étiquette cohérent avec lookup caisse pour un choix scanné seul :
 * refus implicite si d'autres options obligatoires restent à compléter.
 */
export function isChoicePriceDeterminateForLabel(options, choixOptionId) {
  const mandatory = mandatoryOptions(options);
  if (mandatory.length === 0) return true;
  const otherMandatory = mandatory.filter((o) => String(o.id) !== String(choixOptionId));
  return otherMandatory.length === 0;
}

/**
 * @typedef {Object} LabelCandidate
 * @property {string} key
 * @property {string} title
 * @property {string} variantLabel
 * @property {string} code
 * @property {number|null} unitPrice
 * @property {boolean} defaultSelected
 * @property {string} kind 'parent' | 'choix'
 */

/**
 * @param {Object} params
 * @param {string} params.produitNom
 * @param {number|string} params.prix
 * @param {number|string|null} [params.prixPromo]
 * @param {{ code?: string }|null} [params.internalBarcode]
 * @param {Array} [params.productOptions]
 * @param {Record<string, { code?: string }>} [params.choixInternalById]
 * @returns {LabelCandidate[]}
 */
export function buildInternalBarcodeLabelCandidates({
  produitNom,
  prix,
  prixPromo,
  internalBarcode,
  productOptions = [],
  choixInternalById = {},
}) {
  const title = produitNom || 'Produit';
  const options = productOptions || [];
  /** @type {LabelCandidate[]} */
  const candidates = [];

  let anyChoixHasBarcode = false;
  for (const opt of options) {
    for (const choix of opt.choix || []) {
      if (choixHasBarcode(choix, choixInternalById)) anyChoixHasBarcode = true;
    }
  }

  const parentCode = internalBarcode?.code || null;
  if (parentCode) {
    const unitPrice = resolveParentBaseUnitPrice(prix, prixPromo);
    candidates.push({
      key: 'parent',
      kind: 'parent',
      title,
      variantLabel: '',
      code: parentCode,
      unitPrice,
      defaultSelected: shouldIncludeParentLabelByDefault(options, {
        parentHasBarcode: true,
        anyChoixHasBarcode,
      }),
    });
  }

  for (const opt of options) {
    for (const choix of opt.choix || []) {
      const code = resolveChoixBarcode(choix, choixInternalById);
      if (!code) continue;
      const priceOk = isChoicePriceDeterminateForLabel(options, opt.id);
      const unitPrice = priceOk
        ? computeChoiceUnitPrice(prix, prixPromo, choix.prix)
        : null;
      candidates.push({
        key: `choix:${choix.id}`,
        kind: 'choix',
        title,
        variantLabel: choix.nom || '',
        code,
        unitPrice,
        defaultSelected: true,
      });
    }
  }

  return candidates;
}

/**
 * @param {LabelCandidate[]} candidates
 * @param {Record<string, boolean>} [selectionByKey]
 */
export function candidatesToPrintLabels(candidates, selectionByKey = {}) {
  return candidates
    .filter((c) => {
      if (Object.prototype.hasOwnProperty.call(selectionByKey, c.key)) {
        return Boolean(selectionByKey[c.key]);
      }
      return c.defaultSelected;
    })
    .map((c) => ({
      title: c.title,
      variantLabel: c.variantLabel,
      priceLabel: formatRetailPriceLabel(c.unitPrice),
      code: c.code,
    }));
}

/** État de sélection par défaut (clé → coché). */
export function defaultLabelSelectionByKey(candidates) {
  /** @type {Record<string, boolean>} */
  const out = {};
  for (const c of candidates) {
    out[c.key] = c.defaultSelected;
  }
  return out;
}
